/**
 * «Движение кадров» — промпт КЛИПА на шаге «2 · Видео» (задача Оли №335: «на этой вкладке должен
 * отображаться промт для видео, сейчас предложить промт на первой вкладке. Т.о. путаются два промта
 * на фото и на видео»).
 *
 * Здесь всё, что про оживление кадра: стартовый фрейм, «Движение» (`shot_video_prompt`), «Камера»
 * (`shot_camera`) и платная кнопка «предложить промпт». Задание ракурса — промпт ФОТО — осталось на
 * шаге «1 · Ракурсы». Поля правятся той же ручкой правки поля кадра, что и задание ракурса, своего
 * редактора не заводим.
 */
import { useQueryClient } from "@tanstack/react-query";
import { Clapperboard, Sparkles } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router";

import { InlineText } from "@/components/project/InlineText";
import { Btn, Empty, ErrorLine, Hint, Panel } from "@/components/project/ui";
import { mediaUrl } from "@/lib/media";
import { hasStill } from "@/lib/videoCompare";
import { postJson, qk, usd } from "@/lib/project";
import {
  useClipPrompt,
  useEstimate,
  useRunShotClip,
  useVideoOptions,
  videoKeys,
  type ClipPromptResult,
  type TimelineClip,
  type TimelineShot,
  type VideoModel,
} from "@/lib/video";

/** Цена «предложить промпт» — фиксированная, без ручки оценки (`videoclip.CLIP_PROMPT_ESTIMATE`). */
const PRICE = 0.01;

/** Клип этой модели уже в работе — второй раз не ставим: он оплачен и вот-вот придёт. */
const waiting = (clip: TimelineClip | null): boolean => clip?.status === "queued" || clip?.status === "running";

/** Подсказка кнопки «снять клип»: чего не хватает или что именно случится (№348). */
function clipTitle(
  shot: TimelineShot,
  model: VideoModel | null,
  clip: TimelineClip | null,
  loading: boolean,
): string {
  if (!hasStill(shot.still)) return "нужен готовый кадр — он станет стартовым фреймом клипа";
  if (!shot.video_prompt) return "сначала впишите движение — без него модели нечего оживлять";
  if (!shot.duration_ms) return "у кадра нет длительности — задайте её в хронологии или на карточке кадра";
  if (loading) return "загружаю справочник моделей…";
  if (!model) return "модель по умолчанию недоступна: нет discovery — запустите набор в «Прогнать на моделях»";
  if (waiting(clip)) return "клип этой модели уже в очереди — дождитесь ответа";
  if (clip?.status === "done")
    return `ПЛАТНО: ещё один клип на ${model.label}; тот же промпт переснимают кнопкой «перегенерировать» в «Клипах»`;
  return `ПЛАТНО: снять клип этого кадра на ${model.label}`;
}

export function ClipPromptsPanel({ pid, timeline }: { pid: number; timeline: TimelineShot[] }) {
  const client = useQueryClient();
  const clipPrompt = useClipPrompt(pid);
  // Клип одного кадра (№348: «нужна возможность генерировать отдельно каждый»). Модель — та же,
  // что отмечена по умолчанию в «Прогнать на моделях» (`default_on`, с №345 это Kling 3.0 Pro):
  // отдельный выбор модели здесь был бы вторым местом, где её меняют.
  const options = useVideoOptions();
  const model = (options.data?.models ?? []).find((m) => m.default_on && m.discovered) ?? null;
  const runShot = useRunShotClip(pid);
  const running = (shotId: number) => runShot.pending?.shot_ids?.[0] === shotId;
  // Цена КАДРА — только из оценки сервера (бесплатной): `price_15s` каталога — это цена ролика
  // 15 с, то есть трёх клипов, и в кнопке она врала втрое (найдено ревью 16.10).
  const estimate = useEstimate(pid, model ? { models: [model.code], seamless: false, with_refs: false } : null);
  const costOf = (shotId: number) => estimate.data?.items.find((i) => i.shot_id === shotId)?.cost ?? null;
  // Клип этой модели у кадра уже есть: в очереди — ждём, готовый — «ещё один» (это вторая оплата,
  // и кнопка обязана об этом сказать; перегенерация с тем же промптом живёт в «Клипах» ниже).
  const clipOf = (shot: TimelineShot) => (model ? (shot.clips.find((c) => c.model === model.code) ?? null) : null);
  const requestedSid = useRef<number | null>(null);
  const [last, setLast] = useState<{ sid: number; result: ClipPromptResult } | null>(null);
  useEffect(() => {
    if (clipPrompt.data && requestedSid.current !== null) {
      setLast({ sid: requestedSid.current, result: clipPrompt.data });
    }
  }, [clipPrompt.data]);
  const suggest = (sid: number) => {
    requestedSid.current = sid;
    clipPrompt.run(sid);
  };

  const save = async (shotId: number, field: "shot_video_prompt" | "shot_camera", value: string) => {
    const result = await postJson<{ value: string; prompt_resolved: string | null }>(
      `/api/projects/${pid}/shots/${shotId}/field`,
      { field, value },
    );
    void client.invalidateQueries({ queryKey: qk.project(pid) });
    void client.invalidateQueries({ queryKey: qk.shot(pid, shotId) });
    void client.invalidateQueries({ queryKey: videoKeys.poll(pid) });
    // Оценка запуска держит блокер «кадр N: нет видео-промпта» и живёт по своему ключу: без этой
    // строки сотрудник заполняет движение прямо над «Прогнать на моделях», а кнопка запуска
    // остаётся выключенной (найдено ревью 16.10).
    void client.invalidateQueries({ queryKey: ["video-estimate", pid] });
    return result.value;
  };

  return (
    <Panel title="Движение кадров" aside="что будет происходить в клипе" annot="промпты движения">
      {timeline.length === 0 ? (
        <Empty>Кадров пока нет.</Empty>
      ) : (
        <div className="grid gap-2">
          {timeline.map((shot) => (
            <div
              key={shot.shot_id}
              className="flex items-start gap-3 rounded-[var(--radius-ui)] border border-border bg-bg p-2"
            >
              <Link to={`/project/${pid}/shot/${shot.shot_id}`} className="flex-none" title="карточка кадра">
                {hasStill(shot.still) ? (
                  <img
                    src={mediaUrl(shot.still!.asset_path)}
                    alt=""
                    loading="lazy"
                    className="h-[96px] w-[54px] rounded-[3px] border border-border bg-bg-subtle object-contain"
                  />
                ) : (
                  <span className="grid h-[96px] w-[54px] place-items-center rounded-[3px] border border-dashed border-border-strong text-[10px] text-fg-faint">
                    нет кадра
                  </span>
                )}
              </Link>
              <div className="grid min-w-0 flex-1 gap-1 text-[12.5px]">
                <b className="text-fg">
                  {shot.order}. {shot.title}
                </b>
                <label className="grid gap-0.5">
                  <span className="text-[11px] text-fg-faint">движение (по-английски)</span>
                  <InlineText
                    value={shot.video_prompt ?? ""}
                    placeholder="что происходит в кадре за 3–4 секунды"
                    multiline
                    onSave={(v) => save(shot.shot_id, "shot_video_prompt", v)}
                    className="text-[11.5px]"
                    annot={`движение кадра ${shot.order}`}
                  />
                </label>
                <label className="grid gap-0.5">
                  <span className="text-[11px] text-fg-faint">камера</span>
                  <InlineText
                    value={shot.camera ?? ""}
                    placeholder="static, slow dolly-in, handheld follow…"
                    onSave={(v) => save(shot.shot_id, "shot_camera", v)}
                    className="text-[11.5px]"
                    annot={`камера кадра ${shot.order}`}
                  />
                </label>
                {last && last.sid === shot.shot_id ? (
                  <span className="text-[11px] text-fg-faint">
                    предложено: «{last.result.video_prompt}» · камера: {last.result.camera || "—"} ·{" "}
                    {last.result.written ? "записано" : "поля заняты — не тронуты"}
                  </span>
                ) : null}
                <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
                  <Btn
                    tone="primary"
                    disabled={
                      !hasStill(shot.still) ||
                      !shot.video_prompt ||
                      !shot.duration_ms ||
                      !model ||
                      runShot.busy ||
                      waiting(clipOf(shot))
                    }
                    title={clipTitle(shot, model, clipOf(shot), options.isPending)}
                    onClick={() =>
                      model &&
                      runShot.run({ models: [model.code], shot_ids: [shot.shot_id], seamless: false, with_refs: false })
                    }
                  >
                    <Clapperboard size={13} />
                    {running(shot.shot_id)
                      ? "ставлю в очередь…"
                      : waiting(clipOf(shot))
                        ? "клип в очереди…"
                        : `${clipOf(shot)?.status === "done" ? "снять ещё клип" : "снять клип"}${
                            costOf(shot.shot_id) !== null ? ` ≈ ${usd(costOf(shot.shot_id)!, 2)}` : ""
                          }`}
                  </Btn>
                  <Btn
                    tone="ghost"
                    // Мутация одна на панель, поэтому пока ответ в пути гасим ВСЕ кнопки: иначе
                    // оплаченный ответ мог бы по гонке показаться у чужой строки.
                    disabled={!hasStill(shot.still) || clipPrompt.busy}
                    title={
                      hasStill(shot.still)
                        ? "ПЛАТНО ≈ $0.01: композер смотрит на готовый кадр и предлагает движение на 3–4 секунды и ход камеры; пишет только в пустые поля"
                        : "нужен готовый кадр этого кадра ролика — сгенерируйте или выберите его"
                    }
                    onClick={() => suggest(shot.shot_id)}
                  >
                    <Sparkles size={13} />
                    {clipPrompt.busy && clipPrompt.pending === shot.shot_id
                      ? "спрашиваю…"
                      : `предложить промпт ≈ ${usd(PRICE, 2)}`}
                  </Btn>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      <ErrorLine text={runShot.error} className="mt-2" />
      <Hint className="mt-2">
        «снять клип» ставит в очередь клип ОДНОГО кадра на модели по умолчанию; весь набор сразу — ниже, в «Прогнать
        на моделях». «предложить промпт» — композер смотрит на готовый кадр и пишет одно движение на 3–4 секунды и ход камеры:
        место, одежду и лицо не меняет. Заполняет только пустые поля — то, что вы написали руками, не затирается.
      </Hint>
      <ErrorLine text={clipPrompt.error} className="mt-2" />
    </Panel>
  );
}
